import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import styles from "./demo.module.scss";
import { CHANNELS, PHONE_COPY, Phone, cx } from "./index";
import { askExample, fetchExample, trackExample } from "./demoService";
import { BRAND, PRODUCT_URL } from "./offer";

// "How brands use Sonic Prospect": fictional example businesses, one per sector, English and Turkish.
// The chat runs on the same answer path as a customer's assistant. Only daily counts are kept, per
// example and per link label (?ref=, which stays on the address while the visitor switches example).

const COPY = {
  en: {
    phone: PHONE_COPY,
    eyebrow: "Examples · How brands use Sonic Prospect",
    pick: "Pick an industry",
    live: "Live example",
    liveTail: (name) => `Ask ${name} anything`,
    title: (ex) => `${ex.sector_label}: ${ex.name}`,
    lead: (ex) =>
      `${ex.name} is a fictional business we built to show Sonic Prospect at work. The assistant answers only from its own website pages, in the visitor's language, on the website, Instagram and WhatsApp.`,
    knows: (n) => `Knows ${n} pages of the site`,
    does: "What the assistant does",
    allDay: "Answers visitors 24/7",
    channels: "Same assistant on your website, Instagram and WhatsApp",
    offerTitle: "Want this for your business?",
    offer: [
      "We build it from your own website in minutes",
      "Free setup; our team helps you connect your website, WhatsApp and Instagram",
      "Your first 5 new customers are free",
      "Then $4.99 per new customer who books, not per conversation",
    ],
    start: "Build mine from my website",
    call: "Book a call with Pivony",
    fictional: "Example business. Names, prices and policies are invented for this demo.",
    kept: "Chats with this example are kept for 30 days so Pivony can improve it. Please don't share personal details.",
    missing: "This example is not available.",
    down: "Examples are temporarily unavailable. Please try again in a few minutes.",
    docTitle: "Sonic Prospect examples by Pivony",
  },
  tr: {
    phone: {
      learnedFrom: "Kaynak",
      askUs: "Bize her şeyi sorun",
      online: "çevrimiçi",
      tryAsking: "Şunu sorun",
      askPlaceholder: () => "Sorunuzu yazın…",
      tryHint: "Deneyin, kendi sorunuzu yazın",
      composerLabel: "Bir soru sorun",
      send: "Gönder",
      noAnswer: "Buna cevap veremedim.",
      tooMany: "Bir dakikada çok fazla soru geldi. Biraz sonra tekrar deneyin.",
      limitReached:
        "Bu örnek için bugünkü soru hakkınızı kullandınız. Bunun gibi bir asistanı kendi web sitenizden kurun: ilk 5 nitelikli müşteri ücretsiz.",
      limitCta: "Sitemden benimkini kur",
      powered: "Sonic Prospect ile",
    },
    eyebrow: "Örnekler · Markalar Sonic Prospect'i nasıl kullanıyor",
    pick: "Bir sektör seçin",
    live: "Canlı örnek",
    liveTail: () => "Asistana her şeyi sorun",
    title: (ex) => `${ex.sector_label}: ${ex.name}`,
    lead: (ex) =>
      `${ex.name}, Sonic Prospect'i iş başında göstermek için kurguladığımız bir işletme. Asistan yalnızca kendi web sitesi sayfalarından, ziyaretçinin dilinde cevap verir; web sitesinde, Instagram'da ve WhatsApp'ta.`,
    knows: (n) => `Sitenin ${n} sayfasını biliyor`,
    does: "Asistan neler yapar",
    allDay: "Ziyaretçilere 7/24 cevap verir",
    channels: "Web sitesi, Instagram ve WhatsApp'ta aynı asistan",
    offerTitle: "İşletmeniz için ister misiniz?",
    offer: [
      "Kendi web sitenizden dakikalar içinde kuruyoruz",
      "Kurulum ücretsiz; web sitesi, WhatsApp ve Instagram bağlantısında ekibimiz yardım eder",
      "İlk 5 yeni müşteri ücretsiz",
      "Sonrasında sohbet başına değil, randevu alan yeni müşteri başına 4,99 $ ödersiniz",
    ],
    start: "Sitemden benimkini kur",
    call: "Pivony ile görüşme ayarlayın",
    fictional: "Örnek işletme. İsimler, fiyatlar ve politikalar bu demo için uydurulmuştur.",
    kept: "Bu örnekteki sohbetler, Pivony'nin örneği geliştirmesi için 30 gün saklanır. Lütfen kişisel bilgi paylaşmayın.",
    missing: "Bu örnek bulunamadı.",
    down: "Örnekler şu an açılamıyor. Birkaç dakika sonra tekrar deneyin.",
    docTitle: "Pivony Sonic Prospect örnekleri",
  },
};

const DEFAULT_SLUG = { en: "atelier-blanc", tr: "atelier-blanc-tr" };

function preferredLang() {
  try {
    const query = new URLSearchParams(window.location.search).get("lang");
    if (query === "tr" || query === "en") return query;
    return (navigator.language || "").toLowerCase().startsWith("tr") ? "tr" : "en";
  } catch (error) {
    return "en";
  }
}

const ProspectExamples = ({ missing: notFound }) => {
  const { slug: routeSlug } = useParams();
  const history = useHistory();
  const [state, setState] = useState({ loading: true });
  const [channel, setChannel] = useState("website");
  const slug = routeSlug || DEFAULT_SLUG[preferredLang()];
  const ref = new URLSearchParams(useLocation().search).get("ref") || "";

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    fetchExample(slug, { ref, view: true }).then(({ status, body }) => {
      if (alive) setState({ loading: false, status, example: body });
    });
    return () => {
      alive = false;
    };
  }, [slug, ref]);

  const ex = state.example || {};
  const lang = ex.lang === "tr" ? "tr" : "en";
  const copy = COPY[lang];
  useEffect(() => {
    document.title = ex.name ? `${ex.name} · ${copy.docTitle}` : copy.docTitle;
  }, [ex.name, copy]);

  // Bound to the example on screen, not the address: switching example changes the address first,
  // and the phone still showing the old one must not ask its question there.
  const ask = useCallback(
    (question, channelId, turns, auto) => askExample(ex.slug, question, turns, ref, auto, channelId),
    [ex.slug, ref]
  );
  const go = (next) =>
    history.push(`/demos/${encodeURIComponent(next)}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`);

  if (state.loading) return <div className={cx("root")} lang={lang} />;
  if (state.status === 404 && notFound) return notFound;
  if (state.status !== 200) {
    const missing = state.status === 404;
    return (
      <div className={cx("root")} lang={lang}>
        <main className={cx("panel", "gate")}>
          <img className={cx("lockup-full")} src={`${BRAND}/lockup-header.png`} alt="Sonic Prospect by Pivony" />
          <h1>{missing ? copy.missing : copy.down}</h1>
          {missing ? (
            <button type="button" className={cx("btn", "p")} onClick={() => go(DEFAULT_SLUG[lang])}>
              {copy.pick}
            </button>
          ) : null}
        </main>
      </div>
    );
  }

  return (
    <div className={cx("root")} lang={lang}>
      <div className={cx("demo")}>
        <header className={cx("topbar")}>
          <div>
            <p className={cx("prep")}>{copy.eyebrow}</p>
            <nav className={cx("sector-nav")} aria-label={copy.pick}>
              {(ex.examples || []).map((item) => (
                <button
                  type="button"
                  key={item.slug}
                  className={cx("sector", item.slug === ex.slug && "is-on")}
                  aria-current={item.slug === ex.slug ? "page" : undefined}
                  onClick={() => go(item.slug)}
                >
                  {item.sector_label}
                </button>
              ))}
            </nav>
          </div>
          <div className={cx("top-right")}>
            {ex.alternate ? (
              <div className={cx("lang-toggle")} role="group" aria-label="Language">
                <button
                  type="button"
                  className={cx(lang === "en" && "is-on")}
                  onClick={() => lang !== "en" && go(ex.alternate)}
                >
                  EN
                </button>
                <button
                  type="button"
                  className={cx(lang === "tr" && "is-on")}
                  onClick={() => lang !== "tr" && go(ex.alternate)}
                >
                  TR
                </button>
              </div>
            ) : null}
            <img
              className={cx("sp-lockup")}
              src={`${BRAND}/lockup-white@2x.png`}
              srcSet={`${BRAND}/lockup-white.png 1x, ${BRAND}/lockup-white@2x.png 2x`}
              alt="Sonic Prospect by Pivony"
            />
          </div>
        </header>
        <main className={cx("layout")}>
          <section className={cx("left")}>
            <p className={cx("live")}>
              <span className={cx("live-dot")} aria-hidden="true" />
              <b>{copy.live}</b> · {copy.liveTail(ex.name)}
            </p>
            <div className={cx("tabs")} role="tablist">
              {CHANNELS.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  role="tab"
                  aria-selected={channel === item.id ? "true" : "false"}
                  className={cx("tab", channel === item.id && "is-on")}
                  onClick={() => setChannel(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <Phone
              key={ex.slug}
              demo={ex}
              channel={channel}
              ask={ask}
              copy={copy.phone}
              plainSources
              onLimitSignup={() => trackExample(ex.slug, "signup", ref)}
            />
          </section>
          <section className={cx("right")}>
            <h1>{copy.title(ex)}</h1>
            <p className={cx("lead")}>{copy.lead(ex)}</p>
            <div className={cx("card")}>
              <h3>{copy.knows(ex.page_count)}</h3>
              <div className={cx("tags")}>
                {(ex.topics || []).map((topic) => (
                  <span key={topic}>{topic}</span>
                ))}
              </div>
            </div>
            <div className={cx("card")}>
              <h3>{copy.does}</h3>
              <div className={cx("ben")}>
                <span className={cx("ic")} aria-hidden="true">
                  ◷
                </span>
                {copy.allDay}
              </div>
              <div className={cx("ben")}>
                <span className={cx("ic")} aria-hidden="true">
                  ✦
                </span>
                {ex.steer}
              </div>
              <div className={cx("ben")}>
                <span className={cx("ic")} aria-hidden="true">
                  ⇄
                </span>
                {copy.channels}
              </div>
            </div>
            <div className={cx("card", "offer-card")}>
              <h3>{copy.offerTitle}</h3>
              <ul className={cx("offer-list")}>
                {copy.offer.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
            <div className={cx("actions")}>
              <a
                className={cx("btn", "p")}
                href={ex.signup_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExample(ex.slug, "signup", ref)}
              >
                {copy.start}
              </a>
              <a
                className={cx("btn", "s")}
                href={`${PRODUCT_URL}?utm_source=examples&utm_medium=${encodeURIComponent(ex.sector || "")}&utm_campaign=book-call`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackExample(ex.slug, "call", ref)}
              >
                {copy.call}
              </a>
            </div>
            <p className={cx("fictional")}>{copy.fictional}</p>
            <p className={cx("fictional")}>{copy.kept}</p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ProspectExamples;
